import { useState, useEffect } from 'react';
import { MapPin, Search } from 'lucide-react';

// Dichiarazioni TypeScript per Google Maps
declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

interface MapCoordinatePickerProps {
  latitude: number;
  longitude: number;
  onCoordinatesChange?: (lat: number, lng: number) => void;
  onChange?: (lat: number, lng: number) => void; // Alias per onCoordinatesChange
  label?: string;
  disabled?: boolean;
}

interface LatLngLike {
  lat: number;
  lng: number;
}

const DEFAULT_POSITION: LatLngLike = { lat: 40.1689, lng: 15.1428 };
const isDev = Boolean((import.meta as any).env?.DEV);
const logDebug = (...args: any[]) => {
  if (isDev) console.log(...args);
};
const warnDebug = (...args: any[]) => {
  if (isDev) console.warn(...args);
};

const getLatLngFromMarker = (mapMarker: any): LatLngLike | null => {
  if (!mapMarker) return null;

  if (typeof mapMarker.getPosition === 'function') {
    const position = mapMarker.getPosition();
    if (position) return { lat: position.lat(), lng: position.lng() };
  }

  const rawPosition = mapMarker.position;
  if (!rawPosition) return null;

  if (typeof rawPosition.lat === 'function' && typeof rawPosition.lng === 'function') {
    return { lat: rawPosition.lat(), lng: rawPosition.lng() };
  }

  if (typeof rawPosition.lat === 'number' && typeof rawPosition.lng === 'number') {
    return { lat: rawPosition.lat, lng: rawPosition.lng };
  }

  return null;
};

const setMarkerPosition = (mapMarker: any, latLng: any) => {
  if (!mapMarker || !latLng) return;

  if (typeof mapMarker.setPosition === 'function') {
    mapMarker.setPosition(latLng);
    return;
  }

  // AdvancedMarkerElement uses the mutable `position` property.
  if ('position' in mapMarker) {
    mapMarker.position = latLng;
  }
};

const MapCoordinatePicker = ({ 
  latitude, 
  longitude, 
  onCoordinatesChange, 
  onChange,
  label = "Coordinate",
  disabled = false
}: MapCoordinatePickerProps) => {
  const handleCoordinateChange = (lat: number, lng: number) => {
    if (onCoordinatesChange) onCoordinatesChange(lat, lng);
    if (onChange) onChange(lat, lng);
  };
  const [showMap, setShowMap] = useState(false);
  const [searchAddress, setSearchAddress] = useState('');
  const [map, setMap] = useState<any>(null);
  const [marker, setMarker] = useState<any>(null);
  const [initAttempts, setInitAttempts] = useState(0);

  useEffect(() => {
    if (showMap && !map) {
      setInitAttempts(0); // Reset contatore tentativi
      loadGoogleMaps();
    }
  }, [showMap]);

  const loadGoogleMaps = () => {
    const apiKey = (import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey || apiKey === 'your_google_maps_api_key_here') {
      console.error('❌ VITE_GOOGLE_MAPS_API_KEY non configurata. Aggiungi la chiave nel file .env');
      console.error('📖 Vedi webapp-t-alento/GOOGLE_MAPS_SETUP.md per le istruzioni');
      return;
    }

    if (typeof window.google !== 'undefined') {
      // Attendi che il DOM sia pronto
      setTimeout(() => initMap(), 100);
    } else {
      const existing = document.getElementById('google-maps-api-script');
      if (existing) {
        setTimeout(() => initMap(), 100);
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-maps-api-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,marker&loading=async`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Attendi che il DOM sia pronto
        setTimeout(() => initMap(), 100);
      };
      script.onerror = () => {
        console.error('❌ Errore caricamento Google Maps API. Verifica chiave API e ad-blocker/shields.');
      };
      document.head.appendChild(script);
    }
  };

  const initMap = () => {
    const mapElement = document.getElementById('map-picker');
    if (!mapElement) {
      warnDebug('⚠️ Elemento map-picker non trovato nel DOM');
      return;
    }

    // Verifica che Google Maps sia completamente caricato
    if (typeof window.google === 'undefined' || !window.google.maps || !window.google.maps.Map) {
      if (initAttempts < 10) { // Massimo 10 tentativi (5 secondi)
        warnDebug(`⚠️ Google Maps non ancora caricato, tentativo ${initAttempts + 1}/10...`);
        setInitAttempts(prev => prev + 1);
        setTimeout(() => initMap(), 500);
        return;
      } else {
        console.error('❌ Google Maps non caricato dopo 10 tentativi. Verifica la chiave API.');
        return;
      }
    }

    logDebug('🗺️ Inizializzazione mappa Google Maps...');

    const initialPosition = {
      lat: latitude || DEFAULT_POSITION.lat,
      lng: longitude || DEFAULT_POSITION.lng,
    };

    const googleMap = new window.google.maps.Map(mapElement, {
      center: initialPosition,
      zoom: 15,
      mapTypeControl: true,
      streetViewControl: false,
    });

    const AdvancedMarker = window.google.maps?.marker?.AdvancedMarkerElement;
    const googleMarker = AdvancedMarker
      ? new AdvancedMarker({
          position: initialPosition,
          map: googleMap,
          title: 'Trascina per posizionare',
          gmpDraggable: true,
        })
      : new window.google.maps.Marker({
          position: initialPosition,
          map: googleMap,
          draggable: true,
          title: 'Trascina per posizionare',
        });

    googleMarker.addListener('dragend', () => {
      const position = getLatLngFromMarker(googleMarker);
      if (position) {
        handleCoordinateChange(position.lat, position.lng);
      }
    });

    googleMap.addListener('click', (e: any) => {
      if (e.latLng) {
        setMarkerPosition(googleMarker, e.latLng);
        handleCoordinateChange(e.latLng.lat(), e.latLng.lng());
      }
    });

    setMap(googleMap);
    setMarker(googleMarker);
  };

  const handleSearchAddress = async () => {
    if (!map || !searchAddress.trim()) return;

    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: searchAddress }, (results: any, status: any) => {
        if (status === 'OK' && results && results[0]) {
          const location = results[0].geometry.location;
          map.setCenter(location);
          setMarkerPosition(marker, location);
          handleCoordinateChange(location.lat(), location.lng());
        } else {
          alert('Indirizzo non trovato. Prova con un indirizzo diverso.');
        }
      });
    } catch (error) {
      console.error('Errore nella ricerca:', error);
      alert('Errore durante la ricerca dell\'indirizzo.');
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        <MapPin size={16} className="inline mr-2" />
        {label}
      </label>

      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Latitudine</label>
          <input
            type="number"
            step="any"
            value={latitude || ''}
            onChange={(e) => handleCoordinateChange(parseFloat(e.target.value) || 0, longitude)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
            placeholder="40.1689"
            disabled={disabled}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Longitudine</label>
          <input
            type="number"
            step="any"
            value={longitude || ''}
            onChange={(e) => handleCoordinateChange(latitude, parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
            placeholder="15.1428"
            disabled={disabled}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={() => setShowMap(!showMap)}
        disabled={disabled}
        className="w-full mb-3 px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors flex items-center justify-center space-x-2 disabled:bg-gray-200 disabled:text-gray-500 disabled:cursor-not-allowed"
      >
        <MapPin size={16} />
        <span>{showMap ? 'Nascondi Mappa' : 'Seleziona su Mappa'}</span>
      </button>

      {showMap && (
        <div className="border border-gray-300 rounded-lg overflow-hidden">
          {/* Search Bar */}
          <div className="p-3 bg-gray-50 border-b border-gray-300">
            <div className="flex space-x-2">
              <input
                type="text"
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchAddress()}
                placeholder="Cerca indirizzo (es. Via Roma, Casal Velino)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
              />
              <button
                type="button"
                onClick={handleSearchAddress}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Search size={16} />
              </button>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              Clicca sulla mappa o trascina il marker per selezionare le coordinate
            </p>
          </div>

          {/* Map Container */}
          <div id="map-picker" style={{ height: '400px', width: '100%' }} />

          {/* Coordinates Display */}
          <div className="p-3 bg-gray-50 border-t border-gray-300">
            <p className="text-sm text-gray-700">
              <strong>Coordinate selezionate:</strong> {latitude.toFixed(6)}, {longitude.toFixed(6)}
            </p>
            <a
              href={`https://www.google.com/maps?q=${latitude},${longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline"
            >
              Apri in Google Maps →
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapCoordinatePicker;
